import client from "../config/GraphQLApolloClient";
import {gql} from "@apollo/client";
import {PortalUser, randomInt} from "../components";
import dayjs from 'dayjs';
import {setAuthority} from "../utils/authority";
import {VALIDATE_USER} from "../pages/users/queries";
import {Buffer} from "buffer";

const externalRealm = "internetb2x";
const internalRealm = "intranetb2x";
const alphaRealm = "alpha";
const internalReferrer = "bmwgroup.net";
const externalReferrerB2x = "bmw.com";
const externalReferrerSGate = "bmwgroup.com";

const storageKeyPortalParam = "portalParam";
const storageKeyAuthDomainParam = "authDomainParam";
const storageKeyRealmParam = "realmParam";
const sessionStorageKeyCurrentUser = "currentLoggedInUser";
const sessionStorageKeyCurrentUserToken = "currentLoggedInUserToken";
const urlPortalParam = "portal";
const urlAuthDomainParam = "authdomain";
const urlRealmParam = "realm";

let defaultRealm = externalRealm;

// In-memory cache for the auth redirect URL.
// Intentionally NOT stored in localStorage/sessionStorage to avoid cleartext
// storage of sensitive OAuth parameters (CodeQL: js/clear-text-storage-of-sensitive-data).
let cachedAuthRedirectURL: string | null = null;

// In-memory cache for the OAuth realm identifier and its assignment source.
// Intentionally NOT stored in localStorage/sessionStorage to avoid cleartext
// storage of sensitive OAuth routing parameters (CodeQL: js/clear-text-storage-of-sensitive-data).
let cachedRealm: string | null = null;
let cachedRealmAssignedBy: string | null = null;

/** Returns the auth redirect URL held in memory for the current page session. */
export const getAuthRedirectURL = (): string => cachedAuthRedirectURL ?? "";

/** Clears the in-memory auth redirect URL (call on logout / storage clear). */
export const clearAuthRedirectURL = (): void => {
    cachedAuthRedirectURL = null;
};

const QUERY_GET_WEBEAM_CONFIG = gql`
    query OAuth_getWebEamConfiguration($realm: String!) {
        OAuth_getWebEamConfiguration(realm : $realm) {
            url,
            externalUrl,
            defaultRealm,
            enabled
        }
    }
`;

export const QUERY_GET_WEBEAM_SESSION_LOGOUT_URL = gql`
    query OAuth_getWebEamSessionLogoutUrl($tokenId:String!, $realm:String!) {
        OAuth_getWebEamSessionLogoutUrl(tokenId : $tokenId, realm : $realm)
    }
`;


const QUERY_GET_PORTAL_USER_EMAIL = gql`
    query PortalUser_getPortalUserInfoByEmail($email: String!) {
        PortalUser_getPortalUserInfoByEmail(email: $email) {
            user_id
            username
            firstname
            lastname
            email
            roleName

        }
    }
`;

export function setPortalParam(portal: string) {
    localStorage.setItem(storageKeyPortalParam,portal);
}
export function getPortalParam() {
    return localStorage.getItem(storageKeyPortalParam);
}
export function setRealmParam(realm: string){
    localStorage.setItem(storageKeyRealmParam,realm);
}
export function getRealmParam(){
    return localStorage.getItem(storageKeyRealmParam);
}
export function setAuthDomainParam(authDomain: string){
    localStorage.setItem(storageKeyAuthDomainParam,authDomain);
}
export function getAuthDomainParam(){
    return localStorage.getItem(storageKeyAuthDomainParam);
}

export function getLastDocumentReferrer() {
    return localStorage.getItem("lastReferrer");
}

export const isInternalReferrer = (referrer: string|null) => {
    if (!referrer?.trim()) {
        try {
            console.debug('Checking the referrer', referrer);
            const { hostname } = new URL(referrer);
            return (
                hostname === internalReferrer ||
                hostname.endsWith(`.${internalReferrer}`)
            );
        }
        catch(e) {
            console.error(`Error parsing referrer URL:`,referrer,  e.message || e);
        }
    }

    return false;
}

export const isExternalReferrer = (referrer: string|null) => {

    if (!referrer?.trim()) {
        return false;
    }

    try {
        console.debug('Checking the referrer', referrer);
        const { hostname } = new URL(referrer);
        return (
            hostname === externalReferrerB2x ||
            hostname === externalReferrerSGate ||
            hostname.endsWith(`.${externalReferrerB2x}`) ||
            hostname.endsWith(`.${externalReferrerSGate}`)
        );
    } catch (e) {
        console.error(`Error parsing referrer URL:`, referrer, e.message || e);
        return false;
    }
};

export const isValidReferrer = (referrer: string) => {
    return isInternalReferrer(referrer) || isExternalReferrer(referrer);
}

export function getParameterByName(name: string, url = window.location.href) {
    const paramName = name.replace(/[\[\]]/g, "\\$&");
    const regex = new RegExp(`[?&]${paramName}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
}

export function setLastDocumentReferrer() {
    console.log(`Document referrer : ${document.referrer}`);
    if (document.referrer) {//only set referrer if it originated from a bmwgroup.net, bmwgroup.com or bmw.com address.
        if (isValidReferrer(document.referrer)) {
            localStorage.setItem("lastReferrer", document.referrer);
        }
    }
}



export const setRealm = (realm: string, assignedBy: string) => {
    const cleaned = realm?.replace("/", "") ?? null;
    if (cleaned !== cachedRealm) {
        cachedRealm = cleaned;
        cachedRealmAssignedBy = assignedBy;
    }
};



export const getRealm = (doNotUseDefault?: boolean): string => {
    let realm = getParameterByName("realm");//try to read realm from current url.
    let assignedBy = "url";

    if (realm === null || realm === undefined || realm === "") {// if url does not contain a realm, read from in-memory cache (realm is no longer persisted to localStorage).
        realm = cachedRealm ?? "";
        assignedBy = "memory";
    }

    if (realm === null || realm === undefined || realm === "") {//if realm not specified in url or storage - check referrer
        if (getLastDocumentReferrer() != null) {
            if (isExternalReferrer(getLastDocumentReferrer())) {
                realm = externalRealm
                assignedBy = "external referrer";
                console.warn(`last referrer was:: ${getLastDocumentReferrer()} - so setting realm to ${realm}`)
            }
            if (isInternalReferrer(getLastDocumentReferrer())) {
                realm = internalRealm
                assignedBy = "internal referrer";
                console.warn(`last referrer was:: ${getLastDocumentReferrer()} - so setting realm to ${realm}`)
            }
        }
    }
    if (!doNotUseDefault) {
        if (realm === null || realm === undefined || realm === "") {// if url does not contain a realm, use default realm.
            realm = defaultRealm;
            assignedBy = "default bc blank";
        }
        if (realm !== "internetb2x" && realm !== "intranetb2x" && realm !== alphaRealm) {//if invalid value - assume default realm
            assignedBy = `default bc invalid realm ${realm}`;
            realm = defaultRealm;
        }
    }
    setRealm(realm, assignedBy);
    return realm;
};

export const isExternalRealm = () => {
    return (getRealm(true) === externalRealm);
};

export const clearStorage = () => {
    // eslint-disable-next-line no-console
    console.log("Clearing storage");
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("expires_in");
    localStorage.removeItem("portal-Authorized");
    // Remove any legacy realm entries persisted by previous versions of this code.
    localStorage.removeItem("realm");
    localStorage.removeItem("realmAssignedBy");
    sessionStorage.removeItem(sessionStorageKeyCurrentUser);
    sessionStorage.removeItem(sessionStorageKeyCurrentUserToken);
    // Clear all in-memory sensitive caches so they do not linger after logout.
    clearAuthRedirectURL();
    cachedRealm = null;
    cachedRealmAssignedBy = null;
    // sessionStorage.removeItem("callBackAlreadyPerformed")
};

const getCurrentUserSessionToken = (): string | null => {
    return localStorage.getItem("id_token") || localStorage.getItem("access_token");
};

const getCachedCurrentLoggedInUser = (): PortalUser | undefined => {
    const cachedUser = sessionStorage.getItem(sessionStorageKeyCurrentUser);
    const cachedToken = sessionStorage.getItem(sessionStorageKeyCurrentUserToken);
    const currentToken = getCurrentUserSessionToken();

    if (!cachedUser || !cachedToken || !currentToken || cachedToken !== currentToken) {
        return undefined;
    }

    try {
        return JSON.parse(cachedUser) as PortalUser;
    } catch (error) {
        console.error("Unable to parse cached current user", error);
        sessionStorage.removeItem(sessionStorageKeyCurrentUser);
        sessionStorage.removeItem(sessionStorageKeyCurrentUserToken);
        return undefined;
    }
};

const cacheCurrentLoggedInUser = (user: PortalUser) => {
    const currentToken = getCurrentUserSessionToken();

    if (!currentToken) {
        return;
    }

    sessionStorage.setItem(sessionStorageKeyCurrentUser, JSON.stringify(user));
    sessionStorage.setItem(sessionStorageKeyCurrentUserToken, currentToken);
};

export const updateCachedCurrentLoggedInUser = (changes: Partial<PortalUser>) => {
    const cachedUser = getCachedCurrentLoggedInUser();

    if (!cachedUser) {
        return;
    }

    cacheCurrentLoggedInUser({
        ...cachedUser,
        ...changes
    });
};

export const getCurrentLoggedInUserFromDatabase = (user: PortalUser): Promise<any> => {
    return new Promise((resolve, reject) => {
        client
            .query({
                query: QUERY_GET_PORTAL_USER_EMAIL,
                variables: {email: user.email},
                fetchPolicy: "cache-first"
            })
            .then((portalUserData) => {
                if (portalUserData.data.PortalUser_getPortalUserInfoByEmail) {
                    user._id = portalUserData.data.PortalUser_getPortalUserInfoByEmail?.user_id;
                    user.user_id = portalUserData.data.PortalUser_getPortalUserInfoByEmail?.user_id;
                    user.role = portalUserData.data.PortalUser_getPortalUserInfoByEmail?.roleName;
                    setAuthority(portalUserData.data.PortalUser_getPortalUserInfoByEmail?.roleName);
                    resolve(user);

                } else {
                    reject("User not fully registered");
                }
            })
            .catch((err) => {
                console.error(`Error fetching user data from database for email ${user.email}:`, err);
                reject(err);
            });
    });
};

export const getCurrentLoggedInUser = (): Promise<PortalUser> => {
    return new Promise((resolve, reject) => {
        const cachedUser = getCachedCurrentLoggedInUser();
        if (cachedUser) {
            setAuthority(cachedUser.roleName);
            if ((cachedUser as any).realm) {
                setRealm((cachedUser as any).realm, "session cache");
            }
            resolve(cachedUser);
            return;
        }

        client
            .query({
                query: VALIDATE_USER,
                fetchPolicy: "cache-first"
            })
            .then((response) => {
                console.debug('The user', response.data);
                if (response.data.validateUser.success) {
                    const user = {
                        ...response.data.validateUser.portalUser,
                        role: response.data.validateUser.portalUser?.roleName,
                        uid: response.data.validateUser.portalUser?.uid,
                        departmentnumber: response.data.validateUser.portalUser?.departmentnumber,
                        roles: response.data.validateUser.portalUser?.roles,
                        _id: response.data.validateUser?.user_id,
                        active: true,
                        displayName:
                            `${response.data.validateUser?.portalUser.firstname
                            } ${response.data.validateUser?.portalUser.lastname}`,
                        dateFormat: response.data.validateUser?.userPreferences?.dateFormat,
                        decimalSeparator: response.data.validateUser?.userPreferences?.decimalSeparator,
                        thousandSeparator: response.data.validateUser?.userPreferences?.thousandSeparator,
                        userSettingsId: response.data.validateUser?.userPreferences?.userSettingsId,
                        language: response.data.validateUser?.userPreferences?.language
                    };
                    setAuthority(response.data.validateUser?.portalUser.roleName);
                    setRealm(response.data.validateUser?.portalUser.realm, "token");
                    cacheCurrentLoggedInUser(user);
                    resolve(user);
                } else {
                    let errorMessage = undefined;
                    if (response.data.validateUser.errorCode !== undefined && response.data.validateUser.errorCode !== null) {
                        errorMessage = `${response.data.validateUser.errorCode}-${response.data.validateUser.errorDescription}`;
                    }
                    reject({message: errorMessage});
                }
            })
            .catch((err) => {
                reject(err);
            });
    });
};


export function replaceUrlDomain(url: string, domain: string): string {
    const domainRegex = /\.bmw(?:group)?\.(?:com|net)\/auth/;
    return url.replace(domainRegex, `.${domain}/auth`);
}

export const getWeEAMUrl = (): Promise<any> => {
    console.log(`getLastDocumentReferrer: ${getLastDocumentReferrer() }`);
    const currentRealm = getRealm(true);
    console.log(`Current realm for auth: ${currentRealm}`);
    return new Promise((resolve, reject) => {
        client
            .query({
                query: QUERY_GET_WEBEAM_CONFIG,
                variables: {flow: {flowType: "Implicit"}, realm: getRealm(true)},
                fetchPolicy: "cache-first"
            })
            .then((respond) => {
                console.log(`Received response from getWeEAMUrl query:`);
                console.dir(respond);
                if (respond.data.OAuth_getWebEamConfiguration) {
                    defaultRealm = respond.data.OAuth_getWebEamConfiguration.defaultRealm;
                    let authRedirectURL = isExternalRealm() ? respond.data.OAuth_getWebEamConfiguration.externalUrl : respond.data.OAuth_getWebEamConfiguration.url;                    
                    let urlWasSet = false;
                    console.warn(`authRedirectURL is ${authRedirectURL}`);
                    if(getAuthDomainParam() !== null && getAuthDomainParam() !== undefined){
                            const newUrl = replaceUrlDomain(authRedirectURL,getAuthDomainParam());                            
                            console.info(`found auth domain parameter - setting authRedirect url to ${newUrl}`);
                            authRedirectURL = newUrl;
                            urlWasSet = true;
                    }
                    //LEGACY BLOCK
                    if(!urlWasSet){//proceed with legacy redirect. we can remove this once all urls are updated in B2B and SGATE
                        if (getLastDocumentReferrer() != null) {
                            const referrerURL = new URL(getLastDocumentReferrer());
                            console.dir(referrerURL);
                            //redirect urls are setup for bmwgroup.net, if originated from a .com replace the auth url.
                            //both urls share the same jwt keys so safe to switch url.
                            if (referrerURL.hostname.endsWith(`.${externalReferrerSGate}`)) {
                                authRedirectURL = authRedirectURL.replace("bmwgroup.net/auth", externalReferrerSGate.concat("/auth"));
                                authRedirectURL = `${authRedirectURL}&acr_values=strongAuth4000Service`;
                                console.info(`last referrer was:: ${getLastDocumentReferrer()} - so routing to new url ${authRedirectURL}`)
                            }
                            else if (referrerURL.hostname.endsWith(`.${externalReferrerB2x}`)) {
                                authRedirectURL = authRedirectURL.replace("bmwgroup.net/auth", externalReferrerB2x.concat("/auth"));
                                console.info(`last referrer was:: ${getLastDocumentReferrer()} - so routing to new url ${authRedirectURL}`)
                            }
                        }
                        else if (isExternalRealm()) { //is seems like sGate do not set the referrer
                            authRedirectURL = authRedirectURL.replace("bmwgroup.net/auth", externalReferrerSGate.concat("/auth"));
                            // https://atc.bmwgroup.net/confluence/display/WEBEAM/WEN+OpenID+Connect+%28OIDC%29+-+Integration+Guide#WENOpenIDConnect(OIDC)IntegrationGuide-OAuth/OIDCURL-WhitelistforRealm:internetb2x
                            // Adding ocr_value minimum ocr value for string authentication
                            authRedirectURL = `${authRedirectURL}&acr_values=strongAuth4000Service`;
                            console.info(`last referrer was:: ${getLastDocumentReferrer()} - so routing to new url ${authRedirectURL}`)
                        }
                    }
                    //LEGACY BLOCK
                    // Store in-memory only — not in localStorage — to avoid cleartext
                    // storage of sensitive OAuth parameters (CodeQL fix).
                    cachedAuthRedirectURL = authRedirectURL;
                    resolve(authRedirectURL);
                }
            })
            .catch((err) => {
                console.error(`getWeEAMUrl err : ${err}`);
                reject(err);
            });
    });
};


export const generateNonce = (someThing: string) =>
    `${dayjs(new Date()).format("YYYY_MM_DD__HH_mm_ss_SSS")}__${someThing}__${randomInt()}`;


export type jwtPromise = {
    acr: string;
    at_hash: string;
    aud: string;
    auditTrackingId: string;
    auth_time: string;
    azp: string;
    exp: string;
    iat: string;
    iss: string;
    nonce: string;
    realm: string;
    sub: string;
    tokenName: string;
    tokenType: string;
}

export const getJwtPromise = (): jwtPromise => {
    try {
        const jsonData = Buffer.from(localStorage.getItem('id_token').split('.')[1], 'base64');
        return JSON.parse(jsonData.toString());
    } catch (error) {
        console.error("ERROR RETRIEVING JWT PROMISE SECTION");
        console.error(error);
        return undefined;
    }
}

export const storeInitialPathParameters = () =>{
    //store realm
    if(getParameterByName(urlRealmParam) != null){
        setRealmParam(getParameterByName(urlRealmParam));
    }
    //store portal
    if(getParameterByName(urlPortalParam) != null){
        setPortalParam(getParameterByName(urlPortalParam));
    }
    //store authdomain
    if(getParameterByName(urlAuthDomainParam) != null){
        setAuthDomainParam(getParameterByName(urlAuthDomainParam));
    }
}